import { useState } from "react";
import { Send, User, Phone, MapPin, MessageSquare, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    city: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setSubmitStatus("success");
      setFormData({ name: "", phone: "", city: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="section-padding bg-zinc-50" data-testid="contact-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left column - Info */}
          <div>
            <span className="text-yellow-500 font-bold uppercase tracking-wider text-sm mb-4 block">
              Contact
            </span>
            <h2 className="text-4xl md:text-5xl font-bold uppercase tracking-tight text-zinc-900 mb-6 font-heading">
              Besoin d'un serrurier ?
            </h2>
            <p className="text-lg text-zinc-600 mb-8">
              Remplissez ce formulaire et nous vous recontactons dans les plus brefs délais. 
              Pour une urgence, appelez directement le <strong className="text-zinc-900">06 68 03 25 61</strong>.
            </p>

            {/* Map */}
            <div className="rounded-sm overflow-hidden border border-zinc-200 shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2638.4!2d2.5494!3d48.6279!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e5e3e9c9c9c9c9%3A0x0!2s23%20Avenue%20Ren%C3%A9%20Cassin%2C%2077127%20Lieusaint!5e0!3m2!1sfr!2sfr!4v1234567890"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Serrurier Lieusaint - Localisation"
              />
            </div>
          </div>

          {/* Right column - Form */}
          <div className="bg-white border border-zinc-200 rounded-sm p-8 shadow-lg">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-2 text-zinc-700">
                  <User className="w-4 h-4 text-yellow-500" />
                  Nom
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Votre nom"
                  className="h-12 bg-zinc-50 border-zinc-200 focus:border-yellow-400 focus:ring-yellow-400 rounded-sm"
                  data-testid="contact-name-input"
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2 text-zinc-700">
                  <Phone className="w-4 h-4 text-yellow-500" />
                  Téléphone
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Votre numéro de téléphone"
                  className="h-12 bg-zinc-50 border-zinc-200 focus:border-yellow-400 focus:ring-yellow-400 rounded-sm"
                  data-testid="contact-phone-input"
                />
              </div>

              {/* City */}
              <div className="space-y-2">
                <Label htmlFor="city" className="flex items-center gap-2 text-zinc-700">
                  <MapPin className="w-4 h-4 text-yellow-500" />
                  Ville
                </Label>
                <Input
                  id="city"
                  name="city"
                  type="text"
                  required
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Votre ville"
                  className="h-12 bg-zinc-50 border-zinc-200 focus:border-yellow-400 focus:ring-yellow-400 rounded-sm"
                  data-testid="contact-city-input"
                />
              </div>

              {/* Message */}
              <div className="space-y-2">
                <Label htmlFor="message" className="flex items-center gap-2 text-zinc-700">
                  <MessageSquare className="w-4 h-4 text-yellow-500" />
                  Message
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  required
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Décrivez votre besoin..."
                  rows={4}
                  className="bg-zinc-50 border-zinc-200 focus:border-yellow-400 focus:ring-yellow-400 rounded-sm resize-none"
                  data-testid="contact-message-input"
                />
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-wide py-6 rounded-sm"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-2" />
                    Envoyer le message
                  </>
                )}
              </Button>

              {/* Status messages */}
              {submitStatus === "success" && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-sm text-green-700 text-center" data-testid="contact-success-message">
                  Message envoyé avec succès ! Nous vous recontactons rapidement.
                </div>
              )}
              {submitStatus === "error" && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-sm text-red-700 text-center" data-testid="contact-error-message">
                  Une erreur est survenue. Veuillez appeler le 06 68 03 25 61.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
